import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ValidationState,
  DataProfile,
  ProfilingError,
} from "datasets/pages/tables/components/file-validation/types";
import {
  parseValidationJobResults,
  parseProfilingJobResults,
  parseResultsData,
  recoverSourceFileUri,
} from "datasets/pages/tables/components/file-validation/utils/jobParsers";
import {
  JobResponse,
  DataIngestionResponse,
  TaskState,
  type TaskResult,
  type InvalidColumnReason,
  type InvalidColumnHeaderMessage,
  type ValidationMessageGroup,
} from "datasets/pages/tables/types/interfaces/ingestion-job";
import { useFetcher } from "~/hooks/useFetcher";
import { useStartProfiling } from "./useStartProfiling";
import { useStartValidation } from "./useStartValidation";

const { VITE_CATALOG_API } = import.meta.env;

const JOB_POLL_INTERVAL_MS = 2_000;

const INVALID_COLUMN_REASON_LABELS: Record<InvalidColumnReason, string> = {
  NULL: "Empty Column Names",
  LENGTH_EXCEEDED: "Column Name Too Long",
  RESERVED_KEYWORD: "Reserved Keywords",
  INVALID_CHARACTERS: "Invalid Characters",
  DUPLICATE_COLUMN: "Duplicate Columns",
};

const COMPLETED_STATUSES: readonly TaskState[] = ["PASSED", "WARNING", "FAILED"];

/** Returns true when the job or task status indicates completion (PASSED, WARNING, or FAILED). */
const isCompletedStatus = (status?: string): boolean => {
  return status !== undefined && COMPLETED_STATUSES.includes(status as TaskState);
};

interface UseValidationAndProfilingOptions {
  /** The S3 URI of the file to validate/profile. Jobs only start when this is provided. */
  sourceFile?: string | null;
}

export const useValidationAndProfiling = (options: UseValidationAndProfilingOptions = {}) => {
  const { sourceFile } = options;
  const { datasetId: dataset_id } = useParams();
  const [searchParams] = useSearchParams();
  const data_ingestion_id = searchParams.get("ingestion");
  const [validationJobId, setValidationJobId] = useState<string | null>(null);
  const [profilingJobId, setProfilingJobId] = useState<string | null>(null);
  const [hasStartedValidation, setHasStartedValidation] = useState(false);
  const [hasStartedProfiling, setHasStartedProfiling] = useState(false);
  const [validationState, setValidationState] = useState<ValidationState>({
    jobs: [],
    summary: {
      totalRows: 0,
      headerRow: false,
      dataRows: 0,
      errorRows: 0,
      cleanRows: 0,
    },
    isComplete: false,
    hasErrors: false,
    hasWarnings: false,
  });

  const [dataProfile, setDataProfile] = useState<DataProfile | null>(null);
  const [profilingError, setProfilingError] = useState<ProfilingError | null>(null);

  const [isValidating, setIsValidating] = useState(false);
  const [isProfiling, setIsProfiling] = useState(false);

  const { data: ingestion, isLoading: isLoadingIngestion } = useFetcher<DataIngestionResponse>(
    dataset_id && data_ingestion_id
      ? `/api/catalog/datasets/${dataset_id}/ingestions/${data_ingestion_id}`
      : null
  );

  // Fetch existing jobs when we have an ingestion but no sourceFile (page refresh scenario)
  // This allows us to display results from previously started jobs
  const { data: existingJobs } = useFetcher<JobResponse[]>(
    dataset_id && data_ingestion_id && !sourceFile
      ? `${VITE_CATALOG_API}/datasets/${dataset_id}/ingestions/${data_ingestion_id}/jobs`
      : null
  );

  // Source file recovered from job results (page refresh scenario)
  const [recoveredSourceFile, setRecoveredSourceFile] = useState<string | null>(null);

  // Reset state synchronously during render when the ingestion ID changes.
  // This MUST happen before the SWR hooks below compute their keys, otherwise
  // a stale validationJobId paired with a new data_ingestion_id produces
  // a mismatched URL that 404s and breaks the polling cycle.
  const prevIngestionIdRef = useRef<string | null>(null);
  if (
    prevIngestionIdRef.current !== null &&
    data_ingestion_id !== null &&
    prevIngestionIdRef.current !== data_ingestion_id
  ) {
    setValidationJobId(null);
    setProfilingJobId(null);
    setHasStartedValidation(false);
    setHasStartedProfiling(false);
    setIsValidating(false);
    setIsProfiling(false);
    setRecoveredSourceFile(null);
    setValidationState({
      jobs: [],
      summary: {
        totalRows: 0,
        headerRow: false,
        dataRows: 0,
        errorRows: 0,
        cleanRows: 0,
      },
      isComplete: false,
      hasErrors: false,
      hasWarnings: false,
    });
    setDataProfile(null);
    setProfilingError(null);
  }
  prevIngestionIdRef.current = data_ingestion_id;

  // Resume polling for existing jobs on page refresh
  useEffect(() => {
    if (existingJobs && existingJobs.length > 0 && !sourceFile) {
      const validationJob = existingJobs.find((job) => job.job_type === "validation");
      const profilingJob = existingJobs.find((job) => job.job_type === "profiling");

      // Recover the original source file URI from job results (never _VALID_ROWS)
      if (!recoveredSourceFile) {
        const fileUri = recoverSourceFileUri(existingJobs, ingestion ?? undefined);
        if (fileUri) {
          setRecoveredSourceFile(fileUri);
        }
      }

      if (validationJob && !validationJobId) {
        setValidationJobId(validationJob.job_id);
        setHasStartedValidation(true);
        // Check if already complete
        if (!isCompletedStatus(validationJob.status)) {
          setIsValidating(true);
        }
      }

      if (profilingJob && !profilingJobId) {
        setProfilingJobId(profilingJob.job_id);
        setHasStartedProfiling(true);
        // Check if already complete
        if (!isCompletedStatus(profilingJob.status)) {
          setIsProfiling(true);
        }
      }
    }
  }, [existingJobs, ingestion, sourceFile, validationJobId, profilingJobId, recoveredSourceFile]);

  const shouldPollValidation = !!validationJobId && !validationState.isComplete;

  const { data: validationJobData, error: validationJobError } = useFetcher<JobResponse>(
    validationJobId
      ? `${VITE_CATALOG_API}/datasets/${dataset_id}/ingestions/${data_ingestion_id}/jobs/${validationJobId}`
      : null,
    {
      refreshInterval: shouldPollValidation ? JOB_POLL_INTERVAL_MS : 0,
    }
  );

  const shouldPollProfiling = !!profilingJobId && !dataProfile && !profilingError;

  const { data: profilingJobData, error: profilingJobError } = useFetcher<JobResponse>(
    profilingJobId
      ? `${VITE_CATALOG_API}/datasets/${dataset_id}/ingestions/${data_ingestion_id}/jobs/${profilingJobId}`
      : null,
    {
      refreshInterval: shouldPollProfiling ? JOB_POLL_INTERVAL_MS : 0,
    }
  );

  // Handle 404 error for missing job
  useEffect(() => {
    if (validationJobError?.status === 404) {
      console.error("Job not found (404):", validationJobId);
      setValidationState({
        jobs: [
          {
            title: "Job Not Found",
            description: "The validation job could not be found",
            result: "Job ID may be invalid or expired",
            status: "failed",
          },
        ],
        summary: {
          totalRows: 0,
          headerRow: false,
          dataRows: 0,
          errorRows: 0,
          cleanRows: 0,
        },
        isComplete: true,
        hasErrors: true,
        hasWarnings: false,
      });
      setIsValidating(false);
    }
  }, [validationJobError, validationJobId]);

  // Handle 404 error for missing profiling job
  useEffect(() => {
    if (profilingJobError?.status === 404) {
      console.error("Profiling job not found (404):", profilingJobId);
      setDataProfile(null);
      setProfilingError({
        message: "Profiling job could not be found. It may have expired or been deleted.",
        timestamp: new Date().toISOString(),
        retryable: false,
      });
      setIsProfiling(false);
    }
  }, [profilingJobError, profilingJobId]);

  // Update validation state based on job data
  useEffect(() => {
    if (validationJobData) {
      const isComplete = isCompletedStatus(validationJobData.status);

      // Use shared parser for core field extraction
      const parsed = parseValidationJobResults(validationJobData);

      // Job failed with error message but no tasks
      if (parsed.hasErrors && !parsed.tasks) {
        setValidationState({
          jobs: [
            {
              title: "Validation Job Failed",
              description: "The validation job encountered an error",
              result: parsed.errorMessage ?? "Validation job failed",
              status: "failed",
            },
          ],
          summary: {
            totalRows: 0,
            headerRow: false,
            dataRows: 0,
            errorRows: 0,
            cleanRows: 0,
          },
          isComplete: true,
          hasErrors: true,
          hasWarnings: false,
        });
        setIsValidating(false);
        return;
      }

      // No tasks yet — still waiting for results.
      // With the refreshInterval guard above, polling continues until
      // results.data is populated, so this branch is a safety fallback.
      if (!parsed.tasks) {
        if (isComplete) {
          setIsValidating(false);
        }
        return;
      }

      const tasks = parsed.tasks;

      // Map raw tasks to UI-specific ValidationJob cards.
      // The switch on task.message.task_name narrows the discriminated union
      // so each branch has full type safety for that task's fields.
      const jobs = tasks.map((task: TaskResult) => {
        const { state } = task;

        // Determine UI status based on TaskState
        let status: "passed" | "warning" | "failed" | "running";
        if (state === "FAILED") {
          status = "failed";
        } else if (state === "WARNING") {
          status = "warning";
        } else if (!isCompletedStatus(state)) {
          status = "running";
        } else {
          status = "passed";
        }

        switch (task.message.task_name) {
          case "FileTypeValidationTaskMessage": {
            const { is_valid, file_type, invalid_reason } = task.message;
            return {
              title: "File Format Detection",
              description: "Confirming that this is a tabular file",
              result: is_valid ? `File type: ${file_type}` : invalid_reason || "Invalid file type",
              status,
            };
          }

          case "FileHeaderValidationTaskMessage": {
            const {
              has_header,
              valid_columns,
              invalid_columns,
              invalid_columns_messages,
              invalid_reason,
            } = task.message;

            const getHeaderMessage = () => {
              if (invalid_reason) {
                return invalid_reason;
              }
              return "Header detected";
            };

            const messages: string[] = [];
            let messageGroups: ValidationMessageGroup[] | undefined;

            if (invalid_columns_messages && invalid_columns_messages.length > 0) {
              const grouped = new Map<InvalidColumnReason, InvalidColumnHeaderMessage[]>();
              for (const col of invalid_columns_messages) {
                const existing = grouped.get(col.reason) ?? [];
                existing.push(col);
                grouped.set(col.reason, existing);
              }

              messageGroups = Array.from(grouped.entries()).map(([reason, cols]) => ({
                reason,
                label: INVALID_COLUMN_REASON_LABELS[reason],
                description: cols[0].description,
                columns: cols.map((c) => c.column_name || "(empty)"),
              }));
            } else if (invalid_columns.length > 0) {
              messages.push(`Invalid column names: ${invalid_columns.join(", ")}`);
            }

            return {
              title: "Header Detection",
              description: "Detecting if the file has a header row",
              result: getHeaderMessage(),
              status,
              details:
                valid_columns.length > 0
                  ? `Valid column names: ${valid_columns.join(", ")}`
                  : undefined,
              messages: messages.length > 0 ? messages : undefined,
              messageGroups,
            };
          }

          case "FileRowValidationTaskMessage": {
            const { valid_row_count, invalid_row_count, invalid_reason } = task.message;
            const totalRows = valid_row_count + invalid_row_count;

            const getRowMessage = () => {
              if (invalid_reason) {
                return invalid_reason;
              }
              if (totalRows === 0) {
                return "No data rows found in file";
              }
              return `${valid_row_count} valid rows, ${invalid_row_count} invalid rows`;
            };

            const messages: string[] = [];
            if (invalid_row_count > 0) {
              messages.push(
                `${invalid_row_count} row${
                  invalid_row_count > 1 ? "s" : ""
                } could not be parsed correctly`
              );
            }

            return {
              title: "File Structure",
              description: "Analyzing file structure and detecting parsing errors",
              result: getRowMessage(),
              status,
              details: totalRows > 0 ? `Total rows: ${totalRows}` : undefined,
              messages: messages.length > 0 ? messages : undefined,
            };
          }

          default: {
            const msg = task.message as { task_name: string; invalid_reason?: string };
            return {
              title: msg.task_name,
              description: "Validation task",
              result:
                msg.invalid_reason ?? (status === "failed" ? "Task failed" : "Task completed"),
              status,
            };
          }
        }
      });

      // Build summary from specific task types using type-narrowed finds
      const headerTask = tasks.find(
        (t): t is TaskResult & { message: { task_name: "FileHeaderValidationTaskMessage" } } =>
          t.message.task_name === "FileHeaderValidationTaskMessage"
      );
      const rowTask = tasks.find(
        (t): t is TaskResult & { message: { task_name: "FileRowValidationTaskMessage" } } =>
          t.message.task_name === "FileRowValidationTaskMessage"
      );
      const summary = {
        totalRows: rowTask
          ? rowTask.message.valid_row_count + rowTask.message.invalid_row_count
          : 0,
        headerRow: headerTask?.message.has_header ?? false,
        dataRows: rowTask?.message.valid_row_count ?? 0,
        errorRows: rowTask?.message.invalid_row_count ?? 0,
        cleanRows: rowTask?.message.valid_row_count ?? 0,
      };

      // Trust the backend job status as the source of truth for completion.
      // The pipeline may emit fewer tasks on early failure, so we don't
      // assert a specific task count here.
      const shouldMarkComplete = isComplete && tasks.length > 0;

      setValidationState({
        jobs,
        summary,
        isComplete: shouldMarkComplete,
        validatedFilePath: parsed.validatedFilePath ?? undefined,
        invalidRowsFilePath: parsed.invalidRowsFilePath ?? undefined,
        rowDetailsFilePath: parsed.rowDetailsFilePath ?? undefined,
        fileType: parsed.fileType ?? undefined,
        hasErrors: parsed.hasErrors,
        hasWarnings: parsed.hasWarnings,
      });

      if (isComplete) {
        setIsValidating(false);
      }
    }
  }, [validationJobData]);

  // Update profiling state based on job data
  useEffect(() => {
    if (profilingJobData) {
      const isComplete = isCompletedStatus(profilingJobData.status);

      // Use shared parser for core extraction
      const profile = parseProfilingJobResults(profilingJobData, validationState.summary.errorRows);

      if (profile) {
        // Success — shared parser produced a DataProfile
        setDataProfile(profile);
        setProfilingError(null);
        setIsProfiling(false);
      } else if (profilingJobData.results?.data) {
        // Shared parser returned null — determine the specific error for UI
        const parsedResults = parseResultsData(profilingJobData.results.data);

        if (!parsedResults) {
          console.error("Failed to parse profiling results");
          setDataProfile(null);
          setProfilingError({
            message: "Failed to process profiling results. Please try again.",
            timestamp: new Date().toISOString(),
            retryable: true,
          });
          setIsProfiling(false);
        } else if (parsedResults.error) {
          const errorMessage = String(parsedResults.error);
          console.error("Profiling job error:", errorMessage);
          setDataProfile(null);
          setProfilingError({
            message: errorMessage,
            timestamp: new Date().toISOString(),
            retryable: true,
          });
          setIsProfiling(false);
        } else if (parsedResults.state === "FAILED") {
          console.error("Profiling task failed");
          setDataProfile(null);
          setProfilingError({
            message: "Profiling task failed. Please try uploading your file again.",
            timestamp: new Date().toISOString(),
            retryable: true,
          });
          setIsProfiling(false);
        }
        // else: no message yet — still waiting for results
      }

      if (isComplete) {
        setIsProfiling(false);
      }
    }
  }, [profilingJobData, validationState.summary.errorRows]);

  const { startValidation } = useStartValidation();

  // Auto-start validation when ingestion ID and sourceFile are available.
  // We use data_ingestion_id (from URL) rather than the SWR-fetched ingestion
  // object so validation kicks off immediately after creating an ingestion +
  // uploading a file, without waiting for SWR to round-trip.
  useEffect(() => {
    if (
      data_ingestion_id &&
      sourceFile &&
      !hasStartedValidation &&
      !validationJobId &&
      !isValidating
    ) {
      setHasStartedValidation(true);
      setIsValidating(true);

      setRecoveredSourceFile(sourceFile);

      startValidation({
        sourceFile: sourceFile,
      }).then((result) => {
        if (result?.validationJobId) {
          setValidationJobId(result.validationJobId);
        } else {
          setValidationState((prev) => ({
            ...prev,
            hasErrors: true,
            hasWarnings: false,
            isComplete: true,
          }));
          setIsValidating(false);
        }
      });
    }
  }, [
    data_ingestion_id,
    sourceFile,
    hasStartedValidation,
    validationJobId,
    isValidating,
    startValidation,
  ]);

  const { startProfiling } = useStartProfiling();

  // Auto-start profiling after validation has completed successfully
  useEffect(() => {
    if (
      data_ingestion_id &&
      sourceFile &&
      validationState.isComplete &&
      !validationState.hasErrors &&
      !hasStartedProfiling &&
      !isProfiling &&
      !profilingJobId
    ) {
      setHasStartedProfiling(true);
      setIsProfiling(true);

      startProfiling({
        // this should be available of validation passed
        // validation passed -> there's at least one valid row
        sourceFile: validationState.validatedFilePath!,
      }).then((result) => {
        if (result?.profilingJobId) {
          setProfilingJobId(result.profilingJobId);
        } else {
          // Failed to start profiling
          setDataProfile(null);
          setProfilingError({
            message: "Failed to start profiling job. Please try again.",
            timestamp: new Date().toISOString(),
            retryable: true,
          });
          setIsProfiling(false);
        }
      });
    }
  }, [
    isProfiling,
    startProfiling,
    sourceFile,
    data_ingestion_id,
    profilingJobId,
    hasStartedProfiling,
    validationState.isComplete,
    validationState.hasErrors,
    validationState.validatedFilePath,
  ]);

  return {
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
    ingestion,
    isLoadingIngestion,
    /** Source file URI recovered from existing jobs (for page refresh scenario) */
    recoveredSourceFile,
  };
};
